<template>
  <div id="transactions-selection-table" class="overflow-x-auto">
    <DataTable
      :value="selectionTableRowsParsedComputed"
      :tableStyle="tableMinWidthStyle"
      stripedRows
      showGridlines
      size="small"
    >
      <Column field="label" style="width: 18%;">
        <template #header>
          <span class="cell label"></span>
        </template>
        <template #body="slotProps">
          <span class="cell label flex items-center justify-between gap-2">
            <span v-html="slotProps.data.label"></span>
            <Select
              v-if="slotProps.data.key === 'm2hind'"
              v-model="selectedUnitPriceBase"
              :options="m2options"
              optionLabel="text"
              optionValue="value"
              size="small"
            ></Select>
          </span>
        </template>
      </Column>

      <Column
        v-for="field in selectionTableFieldsComputed.filter((f) => f.key !== 'label')"
        :key="field.id"
        :field="field.key"
        :headerStyle="field.thStyle"
      >
        <template #header>
          <ToggleSwitch
            :model-value="getIsComparable(field.id)"
            @update:modelValue="store.toggleTransactionIsComparable(field.id)"
          />
        </template>

        <template #body="slotProps">
          <template v-if="slotProps.data.key === 'ad_link' && slotProps.data[field.key]">
            <a :href="slotProps.data[field.key]" target="_blank" rel="noopener noreferrer" class="block cell break-all">
              {{ slotProps.data[field.key] }}
            </a>
          </template>
          <template v-else-if="ratingFields.includes(slotProps.data.key)">
            <div class="cell editable">
              <RatingMultiSelect
                v-model="slotProps.data[field.key]"
                :field="slotProps.data.key"
                :options="[1, 2, 3, 4, 5]"
                :maxSelectedLabels="2"
                :selectionLimit="2"
                placeholder="Vali kuni 2 hinnangut"
                size="small"
                class="w-full max-w-96"
                @update:model-value="handleCellUpdate(slotProps.data[field.key], field, slotProps.data)"
              />
            </div>
          </template>
          <template v-else-if="parkingFields.includes(slotProps.data.key)">
            <div class="cell editable">
              <RatingMultiSelect
                v-model="slotProps.data[field.key]"
                :field="slotProps.data.key"
                :options="[1, 2, 3, 4, 5, 6, 7, 8, 9]"
                :maxSelectedLabels="3"
                :selectionLimit="3"
                placeholder="Tee kuni 3 valikut"
                size="small"
                class="w-full max-w-96"
                @update:model-value="handleCellUpdate(slotProps.data[field.key], field, slotProps.data)"
              />
            </div>
          </template>
          <template v-else-if="balconyField === slotProps.data.key">
            <div class="cell editable">
              <div class="flex items-center gap-4 w-full max-w-96">
                <span v-if="hasBalcony(field.key)" class=" w-16">
                  {{ hasBalcony(field.key) }} m²
                </span>
                <RatingMultiSelect
                  v-model="slotProps.data[field.key]"
                  :field="slotProps.data.key"
                  :options="[0, 1, 2, 3]"
                  :maxSelectedLabels="3"
                  :selectionLimit="3"
                  placeholder="Tee kuni 3 valikut"
                  size="small"
                  class="w-full max-w-96"
                  @update:model-value="handleCellUpdate(slotProps.data[field.key], field, slotProps.data)"
                />
              </div>
            </div>
          </template>
          <template v-else-if="storageField === slotProps.data.key">
            <div class="cell editable">
              <div class="flex items-center gap-4 w-full max-w-96">
                <RatingMultiSelect
                  v-model="slotProps.data[field.key]"
                  :field="slotProps.data.key"
                  :options="[0, 1, 2]"
                  :maxSelectedLabels="2"
                  :selectionLimit="2"
                  placeholder="Tee kuni 2 valikut"
                  size="small"
                  class="w-full max-w-96"
                  @update:model-value="handleCellUpdate(slotProps.data[field.key], field, slotProps.data)"
                />
              </div>
            </div>
          </template>
          <template v-else-if="isDualAreaCell(slotProps.data, field.key)">
            <div class="cell editable dual-area flex items-center gap-1">
              <editable
                class="p-1 block flex-1 min-w-0"
                @focus="selectContent"
                @blur="(e) => handleDualAreaUpdate(e.target.value.trim(), field.id, snpFieldKey)"
                :model-value="slotProps.data[field.key + dualSnpSuffix]"
              />
              <span class="shrink-0 text-gray-400">/</span>
              <editable
                class="p-1 block flex-1 min-w-0"
                @focus="selectContent"
                @blur="(e) => handleDualAreaUpdate(e.target.value.trim(), field.id, slotProps.data.key)"
                :model-value="slotProps.data[field.key]"
              />
            </div>
          </template>
          <template v-else>
            <template v-if="isCellEditable(slotProps.data.key)">
              <div class="cell editable relative flex items-center">
                <editable
                  :multiline="multilineFields.includes(slotProps.data.key)"
                  :class="`p-1 block cell editable ${
                    slotProps.data.error && slotProps.data.error === field.key ? 'error' : ''
                  }`"
                  @focus="selectContent"
                  @blur="(e) => handleCellUpdate(e.target.value.trim(), field, slotProps.data)"
                  :model-value="slotProps.data[field.key]"
                />
                <span
                  v-if="store.isFieldOverridden(field.key, slotProps.data.key)"
                  class="edited-pill edited-pill-overlay"
                  v-tooltip.top="editedTooltip(field.key, slotProps.data.key)"
                  @click="confirmRevert($event, field.key, slotProps.data.key)"
                >
                  <i class="pi pi-undo"></i>
                </span>
              </div>
            </template>
            <span
              v-else
              :class="`block cell ${
                slotProps.data.error && slotProps.data.error === field.key ? 'error' : ''
              }`"
            >
              {{
                slotProps.data[field.key] === null || slotProps.data[field.key] === ''
                  ? '&nbsp;'
                  : slotProps.data[field.key]
              }}
            </span>
          </template>
        </template>
      </Column>
    </DataTable>

    <!-- RED-297: anchored restore-to-original confirmation (grouped so the
         global ConfirmDialog in AppLayout doesn't also fire) -->
    <ConfirmPopup group="revert-original-selection" />
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { debounce } from 'lodash-es'
import { storeToRefs } from 'pinia'
import { useConfirm } from 'primevue/useconfirm'
import { useI18n } from 'vue-i18n'
import { MARKED_TRANSACTION_NUMERIC_FIELDS, SELECTION_TABLE_UI_ONLY_FIELDS } from '@/constants'
import { DUAL_AREA_SNP_SUFFIX } from '@/services/areaDisplay'
import { isNumeric, selectContent } from '@/services/utilities'
import { useTransactionStore } from '@/stores/transaction'
import { useEvaluationStore } from '@/stores/useEvaluationStore'
import Editable from '../editable.vue'
import RatingMultiSelect from '../RatingMultiSelect.vue'

const { t } = useI18n()

const store = useTransactionStore()
const evaluationStore = useEvaluationStore()

const {
  selectionTableRows,
  selectedUnitPriceBase,
  selectionTableFieldsComputed,
  selectionTableRowsParsedComputed,
  processedSelectionTableRows,
} = storeToRefs(store)

const ratingFields = ['ehitise_seisukord', 'hooneosa_seisukord']

// RED-295: same rows the Kohandustabel wraps (ComparisonTable.vue). The address
// ends with street + house number, so a single-line input clips exactly the part
// the user needs.
const multilineFields = ['aadress', 'tehnovorgud']

// RED-295: floor the column widths (parity with ComparisonTable's
// minmax(280px, 1fr) grid) and let the wrapper scroll horizontally instead of
// crushing the cells on narrow screens. thStyle percentages still distribute
// the width proportionally above this floor.
const tableMinWidthStyle = computed(() => {
  const dataColCount = Math.max(selectionTableFieldsComputed.value.length - 1, 1)
  return { minWidth: `${220 + dataColCount * 280}px` }
})
const parkingFields = ['kinnistu_parkimine']
const balconyField = 'rodu'
const storageField = 'panipaik'

// RED-235: the "Hooneosa pind" row renders two separate editable inputs for an
// üksikelamu column — suletud netopind (left) and eluruumi pind (right) — so the
// user can correct each independently (Toomas Q6). dualCols on the row marks
// which columns are üksikelamu.
const dualSnpSuffix = DUAL_AREA_SNP_SUFFIX
const snpFieldKey = 'ehitise_suletud_netopind'

const isDualAreaCell = (row, columnKey) =>
  Array.isArray(row.dualCols) && row.dualCols.includes(columnKey)

// RED-267: land parcels price per plot m² — the building-area bases (KOM
// pind / Hooneosa pind) make no sense for a maatükk row set. On the
// standalone Tehingute kaart modal the evaluation store carries no land
// flag, so the building options remain the default there.
const m2options = computed(() =>
  evaluationStore.isLandParcel
    ? [
        {
          value: 'maatuki_pindala',
          text: t(`messages.markedtransactions.fields.maatuki_pindala`),
        },
      ]
    : [
        {
          value: 'korteri_pind',
          text: t(`messages.markedtransactions.fields.korteri_pind`),
        },
        {
          value: 'hooneosa_pind',
          text: t(`messages.markedtransactions.fields.hooneosa_pind`),
        },
      ],
)

const hasBalcony = computed(() => {
  return (id) => {
    const row = Object.values(processedSelectionTableRows.value).find(t => t.id === Number(id))

    if (!row) {
      return false
    }

    if (!row['hooneosa_valipindade_pind'] || row['hooneosa_valipindade_pind'] === '-' || row['hooneosa_valipindade_pind'] == '0') {
      return false
    }

    return row['hooneosa_valipindade_pind']
  }
})

const getIsComparable = id => {
  return selectionTableRows.value.find(t => t.id === id)?.is_comparable || false
}

const handleCellUpdate = debounce((value, field, item) => {
  const transactionId = field.id
  const fieldKey = item.key

  if (
    MARKED_TRANSACTION_NUMERIC_FIELDS.includes(fieldKey) &&
    !isNumeric(value)
  ) {
    console.error(`Invalid numeric value for ${fieldKey}: ${value}`)
    // TODO: Add visual error handling
    return
  }

  // RED-297: tehingusumma is an evaluation-scoped override — route it through
  // the shared changes-pivot path (like the RED-235 dual-area fields).
  // updateSelectionTransactionField below persists EHR fields only and would
  // silently drop the edit on reload.
  if (fieldKey === 'tehingusumma') {
    store.updateComparisonTransactionField({ transactionId, fieldKey, value })
    return
  }

  store.updateSelectionTransactionField({
    transactionId,
    fieldKey,
    value,
  })
}, 300)

// RED-235: persist one half of the dual area row. `fieldKey` is either
// 'ehitise_suletud_netopind' (SNP) or 'hooneosa_pind' (eluruumi pind); both are
// areas, so an empty / non-numeric value is rejected like any other numeric cell.
// These are evaluation-scoped corrections, so they go through the same
// changes-pivot persistence the Kohandustabel uses
// (updateComparisonTransactionField → PUT /evaluation/{id}/transaction/{id}/changes).
// updateSelectionTransactionField only saves EHR building / building-part fields,
// so routing the dual area through it would leave the edit in memory only and it
// would vanish on reload. Going through the shared path also keeps the two tables
// in sync.
const handleDualAreaUpdate = debounce((value, transactionId, fieldKey) => {
  if (!isNumeric(value)) {
    console.error(`Invalid numeric value for ${fieldKey}: ${value}`)
    return
  }

  store.updateComparisonTransactionField({ transactionId, fieldKey, value })
}, 300)

// UI-only rows are read-only: an edit would persist as a pivot override.
const isCellEditable = key => {
  return (
    !['id', 'building_id', 'm2hind', 'ad_link'].includes(key) &&
    !SELECTION_TABLE_UI_ONLY_FIELDS.includes(key)
  )
}

// RED-297: edited-cell marker + safe restore-to-original — mirrors
// ComparisonTable (same store state, own ConfirmPopup group).
const confirm = useConfirm()

const formatOriginalValue = value =>
  value === null || value === undefined || value === '' ? '–' : value

const editedTooltip = (columnKey, fieldKey) =>
  t('messages.markedtransactions.edited-tooltip', {
    value: formatOriginalValue(store.getOriginalFieldValue(columnKey, fieldKey)),
  })

const confirmRevert = (event, columnKey, fieldKey) => {
  confirm.require({
    group: 'revert-original-selection',
    target: event.currentTarget,
    message: t('messages.markedtransactions.revert-confirm', {
      value: formatOriginalValue(store.getOriginalFieldValue(columnKey, fieldKey)),
    }),
    icon: 'pi pi-undo',
    acceptLabel: t('messages.markedtransactions.revert-accept'),
    rejectLabel: t('messages.markedtransactions.revert-reject'),
    accept: () => store.revertTransactionField(columnKey, fieldKey),
  })
}
</script>

<style lang="css">
  /* RED-297: duplicated from ComparisonTable.vue — the standalone Tehingute
     kaart modal renders this table without ComparisonTable's styles loaded. */
  .edited-pill {
    flex-shrink: 0;
    font-size: 9px;
    font-weight: 600;
    cursor: pointer;
    padding: 1px 4px;
    margin-right: 4px;
    border-radius: 4px;
    background: #fef3c7;
    color: #b45309;
    border: 1px solid #fcd34d;
  }

  .edited-pill:hover {
    background: #fde68a;
  }

  .edited-pill-overlay {
    position: absolute;
    right: 4px;
    top: 50%;
    transform: translateY(-50%);
    margin-right: 0;
    z-index: 5;
  }

  #transactions-selection-table {
    .p-datatable.p-datatable-gridlines:has(.p-datatable-thead):has(.p-datatable-tbody) .p-datatable-tbody > tr > td:has(.cell) {

      &:has(.editable) {
        background: #f1fff7;
      }

      .cell {
        &.error {
          background: #ffe9e9;
        }

        .m2select {
          width: auto;
        }
      }
    }
  }
</style>
